import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@siaedts/shared/lib/supabase';

type AdminUser = {
  id: string;
  email: string;
  name: string;
  role: string;
};

type AuthContextValue = {
  user: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadUser(sessionUser: { id: string; email?: string; user_metadata?: Record<string, unknown> } | null) {
    if (!sessionUser) {
      setUser(null);
      return;
    }

    try {
      const { data } = await supabase.from('users').select('*').eq('id', sessionUser.id).single();
      const profile = data as { email?: string; name?: string; role?: string } | null;
      setUser({
        id: sessionUser.id,
        email: profile?.email ?? sessionUser.email ?? '',
        name: profile?.name ?? String(sessionUser.user_metadata?.full_name ?? 'Admin User'),
        role: profile?.role ?? String(sessionUser.user_metadata?.role ?? 'viewer'),
      });
    } catch {
      setUser({
        id: sessionUser.id,
        email: sessionUser.email ?? '',
        name: String(sessionUser.user_metadata?.full_name ?? 'Admin User'),
        role: String(sessionUser.user_metadata?.role ?? 'viewer'),
      });
    }
  }

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => loadUser(data.session?.user ?? null)).finally(() => setLoading(false));
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      void loadUser(session?.user ?? null);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  async function login(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: new Error(error.message) };
    if (data.user) {
      await loadUser(data.user);
    }
    return { error: null };
  }

  async function logout() {
    await supabase.auth.signOut();
    setUser(null);
  }

  return <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
